import * as T from "three";
import { logger } from "./log.js";
const log = logger("Water");
const vector = (value, fallback = [0, 0]) => String(value ?? "").match(/[-+]?(?:\d*\.)?\d+(?:e[-+]?\d+)?/gi)?.map(Number) ?? fallback;
const number = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const flag = (value, fallback = false) => value === undefined ? fallback : number(value) !== 0;
function color(value, fallback = [1, 1, 1]) {
    const values = vector(value, fallback), scale = String(value).includes("{") ? 1 / 255 : 1;
    return new T.Color().setRGB(...values.slice(0, 3).map(v => Math.max(0, v * scale)), T.SRGBColorSpace);
}
export function isWater(shader, values) {
    return /^water(?:_dx(?:60|80|81|90|9_hdr)|_dudv)?$/i.test(shader) || flag(values["%compilewater"]) || flag(values["%compileslime"]);
}
export function waterSettings(shader, original) {
    shader = shader.toLowerCase();
    const v = { ...original, ...original[">=dx90"], ...original.water_dx90, ...original.water_dx9_hdr };
    const legacy = !/^water/i.test(shader) || shader === "water_dx60";
    const texturedReflector = !!v.$basetexture && !flag(v.$translucent) && (legacy || v.$refracttexture === undefined);
    const proxies = v.proxies?.$sequence ?? Object.entries(v.proxies ?? {}).map(([type, values]) => ({ type, values }));
    return {
        values: v, proxies, legacy, texturedReflector, above: flag(v.$abovewater, true),
        normalPath: v.$normalmap ?? v.$bumpmap ?? (legacy ? null : "dev/water_normal"),
        normal2Path: v.$bumpmap2, normalMaskPath: v.$bumpmask,
        dudv: shader === "water_dudv" || (!v.$normalmap && /dudv/i.test(v.$bumpmap ?? "")),
        refract: !texturedReflector && v.$refracttexture !== "0" && v.$refracttexture !== "",
        reflect: flag(v.$abovewater, true) && shader !== "water_dudv" && (!legacy || !!v.$envmap),
        forceCheap: legacy || flag(v.$forcecheap), forceExpensive: flag(v.$forceexpensive),
        reflectEntities: flag(v.$reflectentities),
        cheapStart: number(v.$cheapwaterstartdistance, 500), cheapEnd: number(v.$cheapwaterenddistance, 1000),
        refractAmount: number(v.$refractamount, 0), reflectAmount: number(v.$reflectamount, 0.8),
        reflectBlend: number(v.$reflectblendfactor, 1), noFresnel: flag(v.$nofresnel),
        reflectTint: color(v.$reflecttint ?? v.$envmaptint), refractTint: color(v.$refracttint),
        fogColor: color(v.$fogcolor, [0.1, 0.15, 0.12]),
        fogEnabled: flag(v.$fogenable, true), fogStart: number(v.$fogstart), fogEnd: number(v.$fogend, 1000),
        baseTint: color(v.$color ?? v.$color2), baseAlpha: number(v.$alpha, flag(v.$translucent) ? 0.5 : 1),
        scale: vector(v.$scale, [1, 1]), scroll1: vector(v.$scroll1), scroll2: vector(v.$scroll2),
        envContrast: number(v.$envmapcontrast), normalAlphaMask: flag(v.$normalmapalphaenvmapmask),
        blur: flag(v.$blurrefract ?? v.$refractblur ?? v.$bluramount),
    };
}

// Execute the proxy subset used by installed TF2/HL2 water materials, in VMT
// order. Repeated proxy names are distinct operations, not overwritten keys.
export function waterProxyState(settings, seconds, counts = {}) {
    const variables = Object.fromEntries(Object.entries(settings.values).filter(([key]) => key.startsWith("$"))
        .map(([key, value]) => [key, vector(value, [number(value)])]));
    const transforms = {};
    const get = (name, fallback = [0]) => {
        if (name === undefined) return fallback;
        const match = /^(\$[^\[]+)(?:\[(\d+)\])?$/.exec(String(name).toLowerCase());
        if (!match) return vector(name, fallback);
        const value = variables[match[1]] ?? fallback;
        return match[2] === undefined ? value : [value[Number(match[2])] ?? 0];
    };
    const set = (name, value) => {
        const match = /^(\$[^\[]+)(?:\[(\d+)\])?$/.exec(String(name ?? "").toLowerCase());
        if (!match) return;
        if (match[2] === undefined) variables[match[1]] = [...value];
        else (variables[match[1]] ??= [0, 0])[Number(match[2])] = value[0];
    };
    for (const { type, values: p } of settings.proxies) {
        if (type === "animatedtexture") {
            const count = counts[String(p.animatedtexturevar).toLowerCase()] ?? 1;
            set(p.animatedtextureframenumvar, [Math.floor(seconds * number(p.animatedtextureframerate, 30)) % count]);
        } else if (type === "sine") {
            const min = get(p.sinemin, [0])[0], max = get(p.sinemax, [1])[0];
            const phase = (seconds - get(p.timeoffset, [0])[0]) * 2 * Math.PI / Math.max(0.001, get(p.sineperiod, [1])[0]);
            set(p.resultvar, [min + (max - min) * (Math.sin(phase) + 1) / 2]);
        } else if (type === "equals") set(p.resultvar, get(p.srcvar1 ?? p.srcvar));
        else if (type === "texturescroll") {
            const angle = number(p.texturescrollangle) * Math.PI / 180,
                rate = number(p.texturescrollrate), offset = [Math.cos(angle) * rate * seconds, Math.sin(angle) * rate * seconds];
            set(p.texturescrollvar, offset);
            if (/transform/i.test(p.texturescrollvar)) transforms[p.texturescrollvar.toLowerCase()] = new T.Matrix3().setUvTransform(
                ...offset, number(p.texturescale, 1), number(p.texturescale, 1), 0, 0.5, 0.5);
        } else if (type === "texturetransform") {
            const translate = get(p.translatevar, [0, 0]), scale = get(p.scalevar, [1, 1]), center = get(p.centervar, [0.5, 0.5]);
            transforms[p.resultvar.toLowerCase()] = new T.Matrix3().setUvTransform(translate[0], translate[1] ?? translate[0],
                scale[0], scale[1] ?? scale[0], get(p.rotatevar)[0] * Math.PI / 180, center[0], center[1] ?? center[0]);
        }
    }
    for (const name of ["$bumptransform", "$bumptransform2", "$bumpmasktransform", "$basetexturetransform"]) {
        if (transforms[name]) continue;
        const text = settings.values[name];
        const match = /center\s+([\d.-]+)\s+([\d.-]+)\s+scale\s+([\d.-]+)\s+([\d.-]+)\s+rotate\s+([\d.-]+)\s+translate\s+([\d.-]+)\s+([\d.-]+)/i.exec(text ?? "");
        const n = match?.slice(1).map(Number);
        transforms[name] = n ? new T.Matrix3().setUvTransform(n[5], n[6], n[2], n[3], n[4] * Math.PI / 180, n[0], n[1]) : new T.Matrix3();
    }
    return { variables, transforms };
}

const vertexShader = /* glsl */`
    #include <common>
    #include <fog_pars_vertex>
    varying vec2 waterUv;
    varying vec2 lightUv;
    varying vec3 worldPosition;
    varying vec3 surfaceNormal;
    varying vec4 reflectionCoord;
    varying vec4 screenCoord;
    varying float surfaceDepth;
    uniform mat4 reflectionMatrix;
    void main() {
        waterUv = uv;
        lightUv = uv1;
        vec4 world = modelMatrix * vec4(position, 1.0);
        worldPosition = world.xyz;
        surfaceNormal = normalize(mat3(modelMatrix) * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        surfaceDepth = -mvPosition.z;
        screenCoord = projectionMatrix * mvPosition;
        reflectionCoord = reflectionMatrix * world;
        gl_Position = screenCoord;
        #include <fog_vertex>
    }
`;
const fragmentShader = /* glsl */`
    #include <common>
    #include <packing>
    #include <fog_pars_fragment>
    uniform sampler2D normalMap, normalMap2, normalMask, baseMap, lightMap, refractMap, depthMap, reflectMap;
    uniform samplerCube envMap;
    uniform mat3 normalTransform, normalTransform2, normalMaskTransform, baseTransform;
    uniform vec2 uvScale, scroll1, scroll2, captureTexel, cameraRange, cheapRange;
    uniform vec3 reflectTint, refractTint, waterFogColor, baseTint;
    uniform float time, dudv, refractAmount, reflectAmount, reflectBlend, noFresnel, aboveWater;
    uniform float fogEnabled, waterFogStart, waterFogEnd, hasBase, hasLight, baseAlpha;
    uniform float hasEnv, envContrast, normalAlphaMask, useReflection, useRefraction, blurRefraction;
    uniform float forceExpensive, texturedReflector, hasNormal2, hasNormalMask;
    varying vec2 waterUv, lightUv;
    varying vec3 worldPosition, surfaceNormal;
    varying vec4 reflectionCoord, screenCoord;
    varying float surfaceDepth;
    void main() {
        vec2 uv = (normalTransform * vec3(waterUv * uvScale, 1.0)).xy;
        vec4 bump = texture2D(normalMap, uv + scroll1 * time);
        if (length(scroll2) > 0.0) bump = mix(bump, texture2D(normalMap, uv + scroll2 * time), 0.5);
        if (hasNormal2 > 0.5) bump = mix(bump, texture2D(normalMap2,
            (normalTransform2 * vec3(waterUv * uvScale, 1.0)).xy), 0.5);
        if (hasNormalMask > 0.5) {
            vec4 mask = texture2D(normalMask, (normalMaskTransform * vec3(waterUv, 1.0)).xy);
            bump = vec4(mix(mask.rgb, bump.rgb, mask.a), mask.a);
        }
        vec2 wave = bump.rg * 2.0 - 1.0;
        vec3 baseNormal = normalize(surfaceNormal);
        // Derivatives retain the BSP texture-axis directions instead of
        // assuming every map uses the same normal-map rotation.
        vec3 q1 = dFdx(worldPosition), q2 = dFdy(worldPosition);
        vec2 st1 = dFdx(waterUv), st2 = dFdy(waterUv);
        float orientation = st1.x * st2.y - st1.y * st2.x < 0.0 ? -1.0 : 1.0;
        vec3 tangent = normalize(q1 * st2.y - q2 * st1.y) * orientation;
        vec3 bitangent = normalize(-q1 * st2.x + q2 * st1.x) * orientation;
        vec3 normal = normalize(tangent * wave.x + bitangent * wave.y + baseNormal * mix(max(0.05, bump.b * 2.0 - 1.0), 1.0, dudv));
        vec3 eye = normalize(cameraPosition - worldPosition);
        float fresnel = mix(0.02 + 0.98 * pow(1.0 - max(dot(eye, normal), 0.0), 5.0), 1.0, noFresnel);
        vec2 screenUv = screenCoord.xy / screenCoord.w * 0.5 + 0.5;
        vec2 offset = wave * refractAmount * 0.08;
        vec2 refractUv = clamp(screenUv + offset, captureTexel, 1.0 - captureTexel);
        float depth = texture2D(depthMap, refractUv).r;
        float backgroundDepth = -perspectiveDepthToViewZ(depth, cameraRange.x, cameraRange.y);
        // Prevent waves from pulling foreground objects across the shoreline.
        if (backgroundDepth < surfaceDepth) {
            refractUv = screenUv;
            depth = texture2D(depthMap, refractUv).r;
            backgroundDepth = -perspectiveDepthToViewZ(depth, cameraRange.x, cameraRange.y);
        }
        vec3 refraction = texture2D(refractMap, refractUv).rgb;
        if (blurRefraction > 0.5) refraction = (refraction * 2.0 +
            texture2D(refractMap, refractUv + vec2(captureTexel.x, 0.0)).rgb +
            texture2D(refractMap, refractUv - vec2(captureTexel.x, 0.0)).rgb +
            texture2D(refractMap, refractUv + vec2(0.0, captureTexel.y)).rgb +
            texture2D(refractMap, refractUv - vec2(0.0, captureTexel.y)).rgb) / 6.0;
        float thickness = mix(surfaceDepth, max(0.0, backgroundDepth - surfaceDepth), aboveWater)
            * length(cameraPosition - worldPosition) / max(1.0, surfaceDepth);
        float waterFog = fogEnabled * clamp((thickness - waterFogStart) / max(1.0, waterFogEnd - waterFogStart), 0.0, 1.0);
        refraction = mix(refraction * refractTint, waterFogColor, waterFog);
        vec3 environment = waterFogColor;
        if (hasEnv > 0.5) {
            vec3 reflected = reflect(-eye, normal);
            // Source cubemaps use Source world axes; CubeTexture is right handed.
            environment = textureCube(envMap, reflected).rgb;
            environment = mix(environment, environment * environment, envContrast);
        }
        vec2 reflectionUv = reflectionCoord.xy / reflectionCoord.w * 0.5 + 0.5;
        vec3 reflection = environment;
        float reflectionLod = forceExpensive > 0.5 ? 1.0 : 1.0 - smoothstep(cheapRange.x, cheapRange.y, length(cameraPosition - worldPosition));
        if (useReflection > 0.0) reflection = mix(environment, texture2D(reflectMap,
            clamp(reflectionUv + wave * reflectAmount * 0.04, captureTexel, 1.0 - captureTexel)).rgb, useReflection * reflectionLod);
        float mask = mix(1.0, bump.a, normalAlphaMask);
        vec3 result = mix(refraction, reflection * reflectTint, clamp(fresnel * reflectBlend * mask, 0.0, 1.0));
        if (useRefraction < 0.5) result = mix(waterFogColor, reflection * reflectTint, clamp(fresnel * reflectBlend * mask, 0.0, 1.0));
        if (hasBase > 0.5) {
            vec4 base = texture2D(baseMap, (baseTransform * vec3(waterUv, 1.0)).xy);
            vec3 lighting = hasLight > 0.5 ? texture2D(lightMap, lightUv).rgb : vec3(1.0);
            vec3 diffuse = base.rgb * baseTint * lighting;
            // Water also shades opaque reflective floors. Their baked diffuse
            // remains visible beneath the Fresnel reflection, independent of alpha.
            if (texturedReflector > 0.5) result = diffuse + reflection * reflectTint * fresnel * reflectBlend * mask * base.a;
            else result = mix(result, diffuse, base.a * baseAlpha);
        }
        gl_FragColor = vec4(result, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
    }
`;

export class WaterSystem {
    constructor() { this.clear(); }
    clear() {
        for (const target of this.targets ?? []) target.dispose();
        for (const texture of this.defaults ?? []) texture.dispose();
        for (const material of this.clones ?? []) material.dispose();
        this.postMesh?.geometry.dispose(); this.postMesh?.material.dispose();
        this.targets = []; this.defaults = []; this.materials = new Set(); this.surfaces = [];
        this.refraction = null; this.reflections = new Map(); this.underwater = false;
        this.flatNormal = this.white = null; this.clones = []; this.planeMaterials = new Map();
        this.output = this.overlay = this.postMesh = this.postScene = this.postCamera = null;
        this.captured = false;
    }
    defaultTexture(pixel) {
        const texture = new T.DataTexture(new Uint8Array(pixel), 1, 1);
        texture.needsUpdate = true; this.defaults.push(texture); return texture;
    }
    material(settings, normal, base, lightmap, env, normal2 = null, normalMask = null) {
        normal ??= this.flatNormal ??= this.defaultTexture([128, 128, 255, 255]);
        base ??= this.white ??= this.defaultTexture([255, 255, 255, 255]);
        const uniforms = Object.fromEntries(Object.entries({
            normalMap: normal, normalMap2: normal2 ?? normal, normalMask: normalMask ?? normal,
            baseMap: base, lightMap: lightmap ?? base, envMap: env ?? null,
            refractMap: base, reflectMap: base, depthMap: base, reflectionMatrix: new T.Matrix4(),
            normalTransform: new T.Matrix3(), normalTransform2: new T.Matrix3(), normalMaskTransform: new T.Matrix3(), baseTransform: new T.Matrix3(),
            uvScale: new T.Vector2(...settings.scale.slice(0, 2)),
            scroll1: new T.Vector2(...settings.scroll1.slice(0, 2)), scroll2: new T.Vector2(...settings.scroll2.slice(0, 2)),
            captureTexel: new T.Vector2(1 / 512, 1 / 512), cameraRange: new T.Vector2(1, 100000),
            cheapRange: new T.Vector2(settings.cheapStart, Math.max(settings.cheapStart + 1, settings.cheapEnd)),
            reflectTint: settings.reflectTint, refractTint: settings.refractTint,
            waterFogColor: settings.fogColor, baseTint: settings.baseTint,
            time: 0, dudv: +settings.dudv, refractAmount: settings.refractAmount, reflectAmount: settings.reflectAmount,
            reflectBlend: settings.reflect ? settings.reflectBlend : 0, noFresnel: +settings.noFresnel,
            aboveWater: +settings.above, fogEnabled: +settings.fogEnabled,
            waterFogStart: settings.fogStart, waterFogEnd: settings.fogEnd,
            hasBase: +!!settings.values.$basetexture, hasLight: +!!lightmap, baseAlpha: settings.baseAlpha,
            hasEnv: +!!env, envContrast: settings.envContrast, normalAlphaMask: +settings.normalAlphaMask,
            useReflection: 0, useRefraction: +settings.refract, blurRefraction: +settings.blur,
            forceExpensive: +settings.forceExpensive, texturedReflector: +settings.texturedReflector,
            hasNormal2: +!!normal2, hasNormalMask: +!!normalMask,
        }).map(([name, value]) => [name, { value }]));
        const material = new T.ShaderMaterial({ name: "SourceWater", defines: { USE_UV1: "" }, uniforms: { ...T.UniformsUtils.clone(T.UniformsLib.fog), ...uniforms },
            vertexShader, fragmentShader, fog: true, transparent: false, depthWrite: true,
            side: settings.values.$nocull === "1" ? T.DoubleSide : T.FrontSide });
        material.userData.water = { settings, normal, base, normal2 };
        this.materials.add(material);
        return material;
    }
    add(mesh) {
        mesh.geometry.computeBoundingBox();
        const position = new T.Vector3().fromBufferAttribute(mesh.geometry.attributes.position, 0),
            normal = new T.Vector3().fromBufferAttribute(mesh.geometry.attributes.normal, 0).normalize();
        mesh.updateMatrixWorld(); position.applyMatrix4(mesh.matrixWorld);
        normal.applyNormalMatrix(new T.Matrix3().getNormalMatrix(mesh.matrixWorld));
        const plane = new T.Plane().setFromNormalAndCoplanarPoint(normal, position);
        const key = [...normal.toArray(), plane.constant].map(v => v.toFixed(2)).join(","), source = mesh.material,
            materialKey = `${source.uuid}:${key}`;
        if (!this.planeMaterials.has(materialKey)) {
            const uniforms = Object.fromEntries(Object.entries(source.uniforms).map(([name, { value }]) =>
                [name, { value: value?.isTexture ? value : value?.clone ? value.clone() : value }]));
            const material = new T.ShaderMaterial({ name: source.name, defines: { ...source.defines },
                uniforms, vertexShader: source.vertexShader, fragmentShader: source.fragmentShader,
                fog: source.fog, side: source.side, depthWrite: true });
            material.userData.water = source.userData.water;
            this.planeMaterials.set(materialKey, material); this.clones.push(material); this.materials.add(material);
        }
        mesh.material = this.planeMaterials.get(materialKey);
        this.surfaces.push({ mesh, plane, key, bounds: mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld) });
        log.debug("surface registered", { plane: plane.normal.toArray(), constant: plane.constant,
            above: mesh.material.userData.water.settings.above, triangles: mesh.geometry.index.count / 3 });
    }
    target(width, height, depth = false) {
        const target = new T.WebGLRenderTarget(width, height);
        if (depth) target.depthTexture = new T.DepthTexture(width, height, T.UnsignedIntType);
        this.targets.push(target); return target;
    }
    update(seconds, materials = this.materials) {
        for (const material of materials) {
            const { settings, normal, base, normal2 } = material.userData.water;
            const normalFrames = normal.userData.animationFrames ?? [normal], baseFrames = base.userData.animationFrames ?? [base];
            const state = waterProxyState(settings, seconds, { "$normalmap": normalFrames.length, "$bumpmap": normalFrames.length,
                "$bumpmap2": normal2?.userData.animationFrames?.length ?? 1, "$basetexture": baseFrames.length });
            const u = material.uniforms;
            u.normalMap.value = normalFrames[(state.variables.$bumpframe?.[0] ?? 0) % normalFrames.length];
            u.baseMap.value = baseFrames[(state.variables.$frame?.[0] ?? 0) % baseFrames.length];
            u.normalTransform.value.copy(state.transforms.$bumptransform);
            u.normalTransform2.value.copy(state.transforms.$bumptransform2);
            u.normalMaskTransform.value.copy(state.transforms.$bumpmasktransform);
            if (normal2) {
                const frames = normal2.userData.animationFrames ?? [normal2];
                u.normalMap2.value = frames[(state.variables.$bumpframe2?.[0] ?? 0) % frames.length];
            }
            u.baseTransform.value.copy(state.transforms.$basetexturetransform);
            u.time.value = seconds;
        }
    }
    fog(camera, visibility) {
        let submerged = false;
        try {
            const leaf = visibility?.leafAt(camera.position.toArray());
            submerged = leaf >= 0 && !!(visibility.leaves.i32(leaf * visibility.leafStride) & 0x30);
        } catch { /* Missing visibility data does not imply a water volume. */ }
        if (submerged !== this.underwater) { this.underwater = submerged; log.info("water volume changed", { submerged }); }
        this.overlay = null;
        if (!submerged) return null;
        const surface = this.surfaces.filter(s => !s.mesh.material.userData.water.settings.above)
            .sort((a, b) => a.bounds.distanceToPoint(camera.position) - b.bounds.distanceToPoint(camera.position))[0];
        if (!surface) return null;
        const settings = surface.mesh.material.userData.water.settings;
        this.overlay = settings.overlay;
        return settings.fogEnabled ? new T.Fog(settings.fogColor, settings.fogStart, settings.fogEnd) : null;
    }
    outputTarget(renderer) {
        // Keep water captures and the main scene in linear space. Otherwise
        // every visible material switches shader programs twice per frame as
        // Three alternates between render-target and screen color spaces.
        if (!this.overlay && !this.captured) return null;
        const { width, height } = renderer.domElement;
        if (!this.output) {
            this.output = this.target(width, height);
            this.output.texture.type = renderer.extensions.has('EXT_color_buffer_float') ? T.HalfFloatType : T.UnsignedByteType;
            this.output.samples = renderer.getContext().getContextAttributes().antialias ? 4 : 0;
        }
        this.output.setSize(width, height);
        return this.output;
    }
    composite(renderer, now) {
        if (!this.postMesh) {
            this.postCamera = new T.OrthographicCamera(-1, 1, 1, -1, 0, 1);
            this.postScene = new T.Scene();
            this.postMesh = new T.Mesh(new T.PlaneGeometry(2, 2), new T.ShaderMaterial({
                uniforms: { sceneMap: { value: null }, normalMap: { value: null }, transform: { value: new T.Matrix3() },
                    amount: { value: 0 }, tint: { value: new T.Color() }, blur: { value: 0 }, texel: { value: new T.Vector2() } }, depthTest: false, depthWrite: false,
                vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
                fragmentShader: `
                    varying vec2 vUv; uniform sampler2D sceneMap, normalMap; uniform mat3 transform;
                    uniform float amount, blur; uniform vec3 tint; uniform vec2 texel;
                    void main() {
                        vec2 uv = vUv;
                        if (amount != 0.0) {
                            vec2 wave = texture2D(normalMap, (transform * vec3(vUv * 2.0, 1.0)).xy).rg * 2.0 - 1.0;
                            uv = clamp(vUv + wave * amount, 0.001, 0.999);
                        }
                        vec3 color = texture2D(sceneMap, uv).rgb;
                        if (blur > 0.5) color = (color * 2.0 +
                            texture2D(sceneMap, uv + vec2(texel.x, 0.0)).rgb + texture2D(sceneMap, uv - vec2(texel.x, 0.0)).rgb +
                            texture2D(sceneMap, uv + vec2(0.0, texel.y)).rgb + texture2D(sceneMap, uv - vec2(0.0, texel.y)).rgb) / 6.0;
                        gl_FragColor = vec4(color * tint, 1.0);
                        #include <colorspace_fragment>
                    }`,
            }));
            this.postScene.add(this.postMesh);
            log.info(this.overlay ? "underwater composite initialized" : "water output composite initialized");
        }
        const u = this.postMesh.material.uniforms;
        u.sceneMap.value = this.output.texture;
        if (this.overlay) {
            const { settings, normal } = this.overlay, frames = normal.userData.animationFrames ?? [normal],
                state = waterProxyState(settings, now / 1000, { "$normalmap": frames.length, "$bumpmap": frames.length });
            u.normalMap.value = frames[(state.variables.$bumpframe?.[0] ?? 0) % frames.length];
            u.transform.value.copy(state.transforms.$bumptransform);
            u.amount.value = settings.refractAmount; u.tint.value.copy(settings.refractTint);
            u.blur.value = +settings.blur; u.texel.value.set(1 / this.output.width, 1 / this.output.height);
        } else {
            u.amount.value = u.blur.value = 0; u.tint.value.setRGB(1, 1, 1);
        }
        renderer.setRenderTarget(null); renderer.clear(); renderer.render(this.postScene, this.postCamera);
    }
    render(view, now) {
        this.captured = false;
        if (!this.surfaces.length) return;
        const { renderer, camera } = view;
        camera.updateMatrixWorld();
        const vp = new T.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse), frustum = new T.Frustum().setFromProjectionMatrix(vp);
        const visible = this.surfaces.filter(s => s.mesh.visible && s.mesh.layers.test(camera.layers) && frustum.intersectsObject(s.mesh));
        if (!visible.length) return;
        // Animate only materials that can contribute to this frame.
        this.update(now / 1000, new Set(visible.map(s => s.mesh.material)));
        const width = Math.min(1024, Math.max(64, Math.floor(renderer.domElement.width / 2))),
            height = Math.min(1024, Math.max(64, Math.floor(renderer.domElement.height / 2)));
        const refracting = visible.some(surface => surface.mesh.material.userData.water.settings.refract);
        if (refracting) {
            this.refraction ??= this.target(width, height, true);
            this.refraction.setSize(width, height);
        }
        const oldTarget = renderer.getRenderTarget(), oldPlanes = renderer.clippingPlanes,
            viewport = renderer.getViewport(new T.Vector4()), scissor = renderer.getScissor(new T.Vector4()), scissorTest = renderer.getScissorTest();
        const hidden = this.surfaces.map(s => [s.mesh, s.mesh.visible]);
        for (const [mesh] of hidden) mesh.visible = false;
        try {
            // One opaque color/depth capture serves every water surface.
            if (refracting) { view.captureWaterScene(camera, this.refraction, []); this.captured = true; }
            for (const material of new Set(visible.map(surface => surface.mesh.material))) {
                const u = material.uniforms;
                if (refracting) {
                    u.refractMap.value = this.refraction.texture; u.depthMap.value = this.refraction.depthTexture;
                }
                u.cameraRange.value.set(camera.near, camera.far); u.captureTexel.value.set(1 / width, 1 / height);
                u.useReflection.value = 0;
            }
            const planes = new Map();
            for (const surface of visible.sort((a, b) =>
                Math.abs(a.plane.distanceToPoint(camera.position)) - Math.abs(b.plane.distanceToPoint(camera.position)))) {
                const settings = surface.mesh.material.userData.water.settings;
                if (!settings.reflect || settings.reflectBlend <= 0 || settings.forceCheap || surface.plane.distanceToPoint(camera.position) <= 0) continue;
                if (!planes.has(surface.key)) planes.set(surface.key, surface);
            }
            // Bound reflection cost on maps with many pools. Remaining surfaces
            // keep their cubemap/cheap-water shading rather than stale captures.
            for (const surface of [...planes.values()].slice(0, 2)) {
                const settings = surface.mesh.material.userData.water.settings,
                    distance = Math.min(...visible.filter(entry => entry.key === surface.key).map(entry => entry.bounds.distanceToPoint(camera.position)));
                const blend = settings.forceExpensive || distance < settings.cheapEnd ? 1 : 0;
                if (blend <= 0) continue;
                let capture = this.reflections.get(surface.key);
                if (!capture) {
                    // Reuse the bounded pool when moving between disjoint pools.
                    if (this.reflections.size >= 2) {
                        const key = [...this.reflections.keys()].find(key => ![...planes.keys()].slice(0, 2).includes(key));
                        capture = this.reflections.get(key); this.reflections.delete(key);
                    }
                    capture ??= { target: this.target(width, height), camera: new T.PerspectiveCamera() };
                    this.reflections.set(surface.key, capture);
                    log.info("reflection capture allocated", { plane: surface.key, width, height });
                }
                capture.target.setSize(width, height);
                const mirror = capture.camera, plane = surface.plane, direction = new T.Vector3();
                mirror.copy(camera);
                mirror.position.copy(camera.position).addScaledVector(plane.normal, -2 * plane.distanceToPoint(camera.position));
                camera.getWorldDirection(direction); direction.reflect(plane.normal);
                mirror.up.set(0, 1, 0).transformDirection(camera.matrixWorld).reflect(plane.normal);
                mirror.lookAt(mirror.position.clone().add(direction)); mirror.updateMatrixWorld();
                const reflectionMatrix = new T.Matrix4().multiplyMatrices(mirror.projectionMatrix, mirror.matrixWorldInverse);
                // Retain map PVS and deliberate hidden/debug/live-owned state.
                const props = [];
                if (!settings.reflectEntities) {
                    // Avoid walking every live skeleton for every pool. Both
                    // viewers use the same map-prop list; Live adds two roots.
                    for (const mesh of view.propMeshes ?? view.meshes.filter(item => item.mesh.isInstancedMesh).map(item => item.mesh))
                        props.push([mesh, mesh.visible]);
                    // Runtime light entities must still illuminate the world.
                    // Their objects are direct children of an entity group.
                    const lightGroups = new Set((view.lighting?.dynamic ?? []).map(light => light.object.parent));
                    for (const root of [view.liveScene?.root, view.liveScene?.skyRoot]) if (root) {
                        if (root.children.some(child => lightGroups.has(child))) {
                            for (const child of root.children) if (!lightGroups.has(child)) props.push([child, child.visible]);
                        } else props.push([root, root.visible]);
                    }
                    for (const [mesh] of props) mesh.visible = false;
                }
                try { view.captureWaterScene(mirror, capture.target, [plane.clone()]); this.captured = true; }
                finally { for (const [mesh, visible] of props) mesh.visible = visible; }
                for (const entry of visible.filter(entry => entry.key === surface.key)) {
                    if (entry.mesh.material.userData.water.settings.forceCheap) continue;
                    entry.mesh.material.uniforms.reflectMap.value = capture.target.texture;
                    entry.mesh.material.uniforms.reflectionMatrix.value.copy(reflectionMatrix);
                    entry.mesh.material.uniforms.useReflection.value = blend;
                }
            }
        } finally {
            for (const [mesh, visible] of hidden) mesh.visible = visible;
            renderer.clippingPlanes = oldPlanes; renderer.setRenderTarget(oldTarget);
            renderer.setViewport(viewport); renderer.setScissor(scissor); renderer.setScissorTest(scissorTest);
        }
    }
}
